import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../api'


type HealthEntry = {
  account_email: string
  kind: string
  has_credentials: boolean
  latest_run_status: string | null
  latest_run_started_at: string | null
  latest_run_error: string
  last_success_at: string | null
  seconds_since_last_success: number | null
}

type HealthResp = {
  is_healthy: boolean
  staleness_threshold_seconds: number
  checked_at: string
  entries: HealthEntry[]
  reasons: string[]
}

type SyncRun = {
  id: number
  account_email: string
  kind: string
  status: string
  cursor_before: string
  cursor_after: string
  items_seen: number
  items_created: number
  items_updated: number
  items_skipped: number
  error_message: string
  notes: string
  started_at: string
  finished_at: string | null
}


export default function SyncTab() {
  const health = useQuery({
    queryKey: ['syncHealth'],
    queryFn: async () => (await api.get<HealthResp>('/sync/health/')).data,
    refetchInterval: 10_000,
  })
  const runs = useQuery({
    queryKey: ['syncRuns'],
    queryFn: async () => (await api.get<{ results: SyncRun[] }>('/sync/runs/')).data.results,
    refetchInterval: 10_000,
  })

  return (
    <div className="me-scroll h-full overflow-y-auto px-4 py-3 text-[13px]">
      <h2 className="mb-3 text-[15px] font-semibold">Workspace sync health</h2>
      {health.isLoading && <div className="me-text-mute">Loading…</div>}
      {health.data && (
        <>
          <div className={
            'mb-3 rounded-md p-3 ' +
            (health.data.is_healthy
              ? 'bg-green-100 dark:bg-green-950/40'
              : 'bg-red-100 dark:bg-red-950/40')
          }>
            <div className="font-semibold">
              {health.data.is_healthy ? 'Healthy' : 'UNHEALTHY'}
            </div>
            {health.data.reasons.length > 0 && (
              <ul className="mt-2 list-disc pl-5 text-[12px]">
                {health.data.reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            )}
            <div className="me-text-mute mt-2 text-[11px]">
              Staleness threshold: {health.data.staleness_threshold_seconds}s.
              Checked {new Date(health.data.checked_at).toLocaleString()}.
            </div>
          </div>
          <table className="mb-6 w-full text-[12px]">
            <thead className="bg-[color:var(--me-bg)]">
              <tr>
                <th className="border me-border px-2 py-1 text-left">Account</th>
                <th className="border me-border px-2 py-1 text-left">Kind</th>
                <th className="border me-border px-2 py-1 text-left">Latest status</th>
                <th className="border me-border px-2 py-1 text-left">Last successful sync</th>
                <th className="border me-border px-2 py-1 text-left">Error (if any)</th>
              </tr>
            </thead>
            <tbody>
              {health.data.entries.map((e, i) => (
                <tr key={i}>
                  <td className="border me-border px-2 py-1">{e.account_email}</td>
                  <td className="border me-border px-2 py-1">{e.kind}</td>
                  <td className="border me-border px-2 py-1">
                    {e.has_credentials
                      ? (e.latest_run_status || 'no runs yet')
                      : <span className="text-red-600">no OAuth tokens</span>}
                  </td>
                  <td className="border me-border px-2 py-1">
                    {e.last_success_at
                      ? `${new Date(e.last_success_at).toLocaleString()} (${e.seconds_since_last_success}s ago)`
                      : <span className="me-text-mute">never</span>}
                  </td>
                  <td className="border me-border px-2 py-1 text-red-700 dark:text-red-400">
                    {e.latest_run_error}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h2 className="mb-2 text-[15px] font-semibold">Recent sync runs</h2>
      <RecentRunsPanel runs={runs.data ?? []} />
    </div>
  )
}


function RecentRunsPanel({ runs }: { runs: SyncRun[] }) {
  const [accountFilter, setAccountFilter] = useState<string>('')
  const [kindFilter, setKindFilter] = useState<string>('')

  // Flash newly-arrived run rows for 3s so an update is visually obvious.
  // We remember the ids we've ever seen; ids missing from the previous
  // render but present now get added to `flashIds`, which the row uses
  // to key a one-shot CSS animation. Doesn't fire on first paint (avoids
  // flashing every row when the panel mounts).
  const seenIdsRef = useRef<Set<number> | null>(null)
  const [flashIds, setFlashIds] = useState<Set<number>>(new Set())
  useEffect(() => {
    const currentIds = new Set(runs.map((r) => r.id))
    if (seenIdsRef.current === null) {
      seenIdsRef.current = currentIds
      return
    }
    const fresh: number[] = []
    currentIds.forEach((id) => { if (!seenIdsRef.current!.has(id)) fresh.push(id) })
    seenIdsRef.current = currentIds
    if (fresh.length === 0) return
    setFlashIds((prev) => {
      const next = new Set(prev)
      fresh.forEach((id) => next.add(id))
      return next
    })
    const timer = window.setTimeout(() => {
      setFlashIds((prev) => {
        const next = new Set(prev)
        fresh.forEach((id) => next.delete(id))
        return next
      })
    }, 3000)
    return () => window.clearTimeout(timer)
  }, [runs])

  const { accounts, kinds } = useMemo(() => {
    const a = new Set<string>()
    const k = new Set<string>()
    runs.forEach((r) => { a.add(r.account_email); k.add(r.kind) })
    return { accounts: Array.from(a).sort(), kinds: Array.from(k).sort() }
  }, [runs])

  const filtered = useMemo(() => runs.filter((r) =>
    (!accountFilter || r.account_email === accountFilter)
    && (!kindFilter || r.kind === kindFilter)
  ), [runs, accountFilter, kindFilter])

  // Totals across the visible (filtered) rows so you can see "how many
  // things came in during this slice." Skipped is per-item errors, not
  // per-run skips (skipped_locked runs report 0 items_skipped).
  const totals = useMemo(() => {
    let seen = 0, created = 0, updated = 0, skipped = 0
    filtered.forEach((r) => {
      seen += r.items_seen; created += r.items_created
      updated += r.items_updated; skipped += r.items_skipped
    })
    return { seen, created, updated, skipped }
  }, [filtered])

  return (
    <>
      <div className="mb-2 flex flex-wrap items-center gap-2 text-[12px]">
        <label className="me-text-mute">Account</label>
        <select
          className="me-border rounded border bg-[color:var(--me-surface)] px-2 py-0.5 text-[color:var(--me-text)]"
          value={accountFilter}
          onChange={(e) => setAccountFilter(e.target.value)}
        >
          <option value="">All</option>
          {accounts.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <label className="me-text-mute ml-2">Kind</label>
        <select
          className="me-border rounded border bg-[color:var(--me-surface)] px-2 py-0.5 text-[color:var(--me-text)]"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
        >
          <option value="">All</option>
          {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        {(accountFilter || kindFilter) && (
          <button
            className="me-text-mute ml-1 underline"
            onClick={() => { setAccountFilter(''); setKindFilter('') }}
          >
            clear
          </button>
        )}
        <span className="me-text-mute ml-auto text-[11px]">
          Latest 200 rows · auto-refresh 10s
        </span>
      </div>
      <div className="me-text-mute mb-2 text-[11px]">
        <span className="me-mono">{filtered.length}</span> run{filtered.length === 1 ? '' : 's'} shown ·
        totals: seen <span className="me-mono">{totals.seen}</span>,
        created <span className="me-mono">{totals.created}</span>,
        updated <span className="me-mono">{totals.updated}</span>,
        skipped <span className="me-mono">{totals.skipped}</span>
      </div>
      <table className="w-full text-[12px]">
        <thead className="bg-[color:var(--me-bg)]">
          <tr>
            <th className="border me-border px-2 py-1 text-left">Started</th>
            <th className="border me-border px-2 py-1 text-left">Account</th>
            <th className="border me-border px-2 py-1 text-left">Kind</th>
            <th className="border me-border px-2 py-1 text-left">Status</th>
            <th className="border me-border px-2 py-1 text-right">
              <span className="group relative cursor-help border-b border-dotted me-border">
                Seen / Created / Updated / Skipped
                <span className="pointer-events-none absolute right-0 top-full z-30 mt-1 hidden w-80 whitespace-normal rounded-md border me-border bg-[color:var(--me-surface)] p-2.5 text-left text-[11px] font-normal me-text shadow-lg group-hover:block">
                  <span className="mb-1 block"><b>Seen</b> — messages the delta touched</span>
                  <span className="mb-1 block"><b>Created</b> — genuinely new artifacts</span>
                  <span className="mb-1 block"><b>Updated</b> — a message we already had got re-fetched (labels / metadata changed)</span>
                  <span className="block"><b>Skipped</b> — seen but not ingested (a draft / chat / filtered message, or an attachment fetch that failed without killing the message)</span>
                </span>
              </span>
            </th>
            <th className="border me-border px-2 py-1 text-left">Cursor before → after</th>
            <th className="border me-border px-2 py-1 text-left">Notes / error</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((r) => (
            <tr key={r.id} className={
              (r.status === 'failed' ? 'bg-red-50 dark:bg-red-950/20 ' : '')
              + (flashIds.has(r.id) ? 'me-flash-new' : '')
            }>
              <td className="me-mono border me-border px-2 py-1">{new Date(r.started_at).toLocaleString()}</td>
              <td className="border me-border px-2 py-1">{r.account_email}</td>
              <td className="me-mono border me-border px-2 py-1">{r.kind}</td>
              <td className="border me-border px-2 py-1">{r.status}</td>
              <td className="me-mono border me-border px-2 py-1 text-right">
                {r.items_seen} / {r.items_created} / {r.items_updated} / {r.items_skipped}
              </td>
              <td className="me-mono border me-border px-2 py-1 text-[11px]">
                {r.cursor_before || <span className="me-text-mute">-</span>} → {r.cursor_after || <span className="me-text-mute">-</span>}
              </td>
              <td className="border me-border px-2 py-1 text-[11px]">
                {r.error_message || r.notes}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}
